<template>
    <div class="register-select">
        <div class="register-select__header">
            <h1 class="register-select__title title title_big">Choose the product</h1>
        </div>

        <!-- Already attached warning -->
        <div v-if="alreadyAttached" class="register-select__warning">
            <p class="register-select__warning-text">
                This label is already attached to another product.
            </p>
            <RouterLink
                v-if="alreadyAttached.existing_product_id"
                to="/product_info"
                class="register-select__warning-link"
                @click="openAlreadyAttachedProduct"
            >
                View attached product
            </RouterLink>
        </div>

        <!-- Generic attach error -->
        <div v-if="attachError" class="register-select__error">
            {{ attachError }}
        </div>

        <!-- Products list -->
        <div v-if="isLoadingProducts" class="register-select__loading">
            <img src="@/assets/icons/loader_scan.svg" class="register-select__spinner" />
            <span>Loading products...</span>
        </div>

        <div v-else-if="productLoadError" class="register-select__error">
            {{ productLoadError }}
            <button class="btn register-select__retry" @click="loadProducts">Retry</button>
        </div>

        <div v-else-if="products.length" class="register-select__list">
            <button
                v-for="product in products"
                :key="product.id"
                class="product-card"
                type="button"
                @click="selectProduct(product)"
            >
                <div class="product-card__info">
                    <div class="product-card__name">
                        {{ product.name }}
                        <template v-if="product.year"> {{ product.year }}</template>
                    </div>
                </div>
                <div class="product-card__img-wrap">
                    <img
                        :src="productImage(product)"
                        :alt="product.name"
                        class="product-card__img"
                    />
                </div>
            </button>
            <p class="add-product-tip">
                If you don’t see the needed product, go to admin panel to add it there
            </p>
        </div>

        <div v-else class="empty-product-list">
            <img src="@/assets/images/empty_list.png" alt="" class="empty-product-list__img" />
            <h3 class="empty-product-list__title">The product list is empty</h3>
            <div class="empty-product-list__content">
                If you don’t see the needed product, go to admin panel to check it there
            </div>
        </div>
        <!-- Confirmation dialog -->
        <Transition name="confirm-modal">
            <div v-if="confirmingProduct" class="confirm-modal">
                <div class="confirm-modal__bg" @click="cancelConfirm" />
                <div class="confirm-modal__content">
                    <div class="confirm-modal__title">Are you sure?</div>
                    <p class="confirm-modal__desc">
                        The label scanned will be assigned to the product
                    </p>
                    <div class="confirm-modal__product">
                        <div class="confirm-modal__product-name">
                            {{ confirmingProduct.name }}
                            <template v-if="confirmingProduct.year">
                                {{ confirmingProduct.year }}</template
                            >
                        </div>
                        <img
                            :src="productImage(confirmingProduct)"
                            :alt="confirmingProduct.name"
                            class="confirm-modal__product-img"
                        />
                    </div>
                    <div class="confirm-modal__actions">
                        <button
                            class="btn confirm-modal__btn-confirm"
                            type="button"
                            :disabled="isAttaching"
                            @click="confirmAttach"
                        >
                            <template v-if="isAttaching">Attaching...</template>
                            <template v-else>Confirm</template>
                        </button>
                        <button
                            class="btn btn_empty btn--secondary confirm-modal__btn-cancel"
                            type="button"
                            :disabled="isAttaching"
                            @click="cancelConfirm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import productPlaceholder from '@/assets/images/product_placeholder.png'
import { apiService, normalizeProduct } from '@/services/api'
import { useRegisterStore } from '@/stores/register'
import type { LabelAlreadyAttachedError, Product } from '@/types'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const registerStore = useRegisterStore()

const products = ref<Product[]>([])
const isLoadingProducts = ref(false)
const productLoadError = ref<string | null>(null)

const confirmingProduct = ref<Product | null>(null)
const isAttaching = ref(false)
const attachError = ref<string | null>(null)
const alreadyAttached = ref<LabelAlreadyAttachedError | null>(null)

onMounted(async () => {
    if (!registerStore.recognizedLabelId) {
        await router.replace({ name: 'register-scan' })
        return
    }
    await loadProducts()
})

const loadProducts = async () => {
    isLoadingProducts.value = true
    productLoadError.value = null
    try {
        const res = await apiService.getProducts({ limit: 200 })
        if (res.success && res.data) {
            products.value = res.data.items.map((p) => normalizeProduct(p))
        } else {
            productLoadError.value = res.error?.message || 'Failed to load products'
        }
    } catch {
        productLoadError.value = 'Failed to load products'
    } finally {
        isLoadingProducts.value = false
    }
}

const selectProduct = (product: Product) => {
    attachError.value = null
    alreadyAttached.value = null
    confirmingProduct.value = product
}

const cancelConfirm = () => {
    confirmingProduct.value = null
    attachError.value = null
    alreadyAttached.value = null
}

const confirmAttach = async () => {
    if (!confirmingProduct.value || !registerStore.recognizedLabelId) return

    isAttaching.value = true
    attachError.value = null
    alreadyAttached.value = null

    const result = await registerStore.attachLabel(
        String(confirmingProduct.value.id),
        registerStore.recognizedLabelId
    )

    isAttaching.value = false

    if (result.success) {
        registerStore.selectedProduct = result.product
        await router.push({ name: 'register-success' })
    } else if ('alreadyAttached' in result && result.alreadyAttached) {
        alreadyAttached.value = result.alreadyAttached
        confirmingProduct.value = null
    } else {
        attachError.value = result.error?.message || 'Failed to attach label'
        confirmingProduct.value = null
    }
}

const productImage = (product: Product) =>
    product.product_image?.url || product.bg_image?.url || productPlaceholder

const openAlreadyAttachedProduct = () => {
    if (alreadyAttached.value?.existing_product) {
        localStorage.setItem(
            'last_scan',
            JSON.stringify({
                product: alreadyAttached.value.existing_product,
                scanned_at: new Date().toISOString(),
            })
        )
    }
}
</script>

<style scoped lang="stylus">
.register-select
    padding 16px 16px calc(var(--app-footer-height) + 16px)

    &__header
        margin-bottom 20px

    &__title
        margin-bottom 6px

    &__subtitle
        font-size 14px
        line-height 20px
        color var(--app-text-grey-color)

    &__loading
        display flex
        flex-direction column
        align-items center
        gap 12px
        padding 40px 0
        color var(--app-text-grey-color)
        font-size 14px

    &__spinner
        width 32px
        height auto
        animation spin 1.5s ease-in-out infinite

    &__error
        padding 12px 16px
        background rgba(255, 80, 80, 0.1)
        border-radius 10px
        color var(--app-error)
        font-size 14px
        line-height 20px
        margin-bottom 16px
        display flex
        flex-direction column
        gap 8px

    &__warning
        padding 12px 16px
        background rgba(255, 180, 0, 0.1)
        border-radius 10px
        margin-bottom 16px

    &__warning-text
        font-size 14px
        line-height 20px
        color var(--app-text-color)
        margin-bottom 6px

    &__warning-link
        font-size 14px
        color var(--app-main-accent-color)
        text-decoration none

    &__retry
        align-self flex-start

    &__list
        display flex
        flex-direction column
        gap 12px

.product-card
    width 100%
    background-color var(--app-bg-secondary)
    border-radius 12px
    padding 12px 0 12px 12px
    display flex
    align-items stretch
    gap 12px
    text-align left
    border none
    cursor pointer
    transition background 0.15s ease

    &:active
        background-color var(--app-border-color)

    &__info
        flex 1
        display flex
        flex-direction column
        justify-content center
        gap 8px

    &__name
        font-size 16px
        line-height 19px
        font-weight 500
        color var(--app-text-color-secondary)
        display -webkit-box
        -webkit-line-clamp 3
        -webkit-box-orient vertical
        overflow hidden

    &__desc
        font-size 12px
        line-height 16px
        color var(--app-text-grey-color)
        display -webkit-box
        -webkit-line-clamp 2
        -webkit-box-orient vertical
        overflow hidden

    &__img-wrap
        flex-shrink 0
        width 60px
        height 75px

    &__img
        object-fit contain
        width 100%
        height 100%

.confirm-modal
    position fixed
    inset 0
    display flex
    align-items flex-end
    justify-content center
    z-index 90
    margin-bottom var(--app-footer-height)

    &__bg
        position absolute
        inset 0
        background rgba(0, 0, 0, 0.5)

    &__content
        position relative
        z-index 10
        width 100%
        background var(--app-bg-color)
        border-radius 16px 16px 0 0
        padding 28px 16px 32px
        display flex
        flex-direction column
        gap 16px

    &__title
        font-size 20px
        line-height 24px
        font-weight 600
        color var(--app-text-color)

    &__desc
        font-size 15px
        line-height 22px
        color var(--app-text-color-secondary)
        margin 0

    &__product
        display flex
        align-items center
        gap 12px
        padding 12px
        background var(--app-bg-secondary)
        border-radius 10px

    &__product-name
        flex 1
        font-size 15px
        line-height 20px
        font-weight 500
        color var(--app-text-color)

    &__product-img
        width 56px
        height 56px
        object-fit contain
        flex-shrink 0

    &__actions
        display flex
        flex-direction column
        gap 10px

    &__btn-confirm
        width 100%

    &__btn-cancel
        width 100%

.add-product-tip
    width 275px
    margin 20px auto 0 auto
    font-size 16px
    line-height 150%
    text-align center
    color var(--app-text-color)

.empty-product-list
    display flex
    flex-direction column
    align-items center
    gap 20px
    margin-top 20vh
    text-align center
    &__img
        width 180px
        height auto
    &__title
        font-weight 600
        font-size 24px
        line-height 150%
        color var(--app-black)
    &__content
        font-size 16px
        line-height 150%
        color var(--app-text-color)

.confirm-modal-enter-active,
.confirm-modal-leave-active
    transition opacity 0.2s ease-out

.confirm-modal-enter-from,
.confirm-modal-leave-to
    opacity 0

.confirm-modal-enter-active .confirm-modal__content,
.confirm-modal-leave-active .confirm-modal__content
    transition transform 0.2s ease-out

.confirm-modal-enter-from .confirm-modal__content,
.confirm-modal-leave-to .confirm-modal__content
    transform translateY(100%)

@keyframes spin
    0%
        transform rotate(0deg)
    100%
        transform rotate(360deg)
</style>
